import React from 'react';
import styled from 'styled-components';
import { 
  TrendingUp, 
  Target, 
  Award, 
  Zap,
  CheckCircle,
  BarChart3,
  Loader2
} from 'lucide-react';
import { theme } from '../styles/theme';
import { ROIAnalysis } from '../types';

const ROIContainer = styled.div`
  background: linear-gradient(135deg, ${theme.colors.success[100]}20, ${theme.colors.primary[100]}20);
  border: 1px solid ${theme.colors.success[500]}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[6]};
  position: relative;
  overflow: hidden;
  margin-bottom: ${theme.spacing[6]};

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, ${theme.colors.success[500]}15 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const ROIHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing[6]};
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
    text-align: center;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const ROIIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[600]});
  border-radius: ${theme.borderRadius.md};
  color: white;
  box-shadow: ${theme.shadows.lg};
`;

const TitleContent = styled.div`
  h2 {
    margin: 0 0 ${theme.spacing[1]} 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.bold};
    font-size: ${theme.typography.fontSize.xl};
  }

  p {
    margin: 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.base};
  }
`;

const ImprovementBadge = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: linear-gradient(135deg, ${theme.colors.success[500]}, ${theme.colors.success[600]});
  color: white;
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  box-shadow: ${theme.shadows.lg};
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: ${theme.shadows.lg};
    }
    50% {
      transform: scale(1.02);
      box-shadow: ${theme.shadows.xl};
    }
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing[4]};
  position: relative;
  z-index: 1;
  margin-bottom: ${theme.spacing[6]};
`;

const MetricCard = styled.div`
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[4]};
  text-align: center;
  box-shadow: ${theme.shadows.sm};
  transition: all ${theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary[500]};
  }
`;

const MetricIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.primary[600]};
  margin: 0 auto ${theme.spacing[3]} auto;
`;

const MetricValue = styled.div<{ $isLoading?: boolean }>`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  
  ${({ $isLoading }) => $isLoading && `
    background: linear-gradient(90deg, 
      ${theme.colors.backgroundSecondary} 25%, 
      ${theme.colors.border} 50%, 
      ${theme.colors.backgroundSecondary} 75%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: ${theme.borderRadius.sm};
    height: 32px;
    color: transparent;
    
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `}
`;

const MetricLabel = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const ConfidenceSection = styled.div`
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[5]};
  position: relative;
  z-index: 1;
`;

const ConfidenceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[4]};
`;

const ConfidenceIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.sm};
  color: white;
`;

const ConfidenceTitle = styled.h3`
  margin: 0;
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.lg};
`;

const ConfidenceContent = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.sm};
  border-left: 4px solid ${theme.colors.success[500]};
`;

const ScoreComparison = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  margin: ${theme.spacing[4]} 0;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.borderRadius.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const ScoreItem = styled.div<{ type: 'baseline' | 'enhanced' }>`
  text-align: center;
  flex: 1;

  .label {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing[1]};
    font-weight: ${theme.typography.fontWeight.medium};
  }

  .score {
    font-size: ${theme.typography.fontSize['3xl']};
    font-weight: ${theme.typography.fontWeight.bold};
    color: ${({ type }) => 
      type === 'enhanced' 
        ? theme.colors.success[600] 
        : theme.colors.primary[600]
    };
    margin-bottom: ${theme.spacing[1]};
  }

  .description {
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.textMuted};
  }
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${theme.colors.success[500]}, ${theme.colors.success[600]});
  border-radius: ${theme.borderRadius.full};
  color: white;
  box-shadow: ${theme.shadows.md};

  @media (max-width: ${theme.breakpoints.sm}) {
    transform: rotate(90deg);
  }
`;

interface ROIAnalysisCardProps {
  roiAnalysis?: ROIAnalysis;
  isLoading?: boolean;
}

const ROIAnalysisCard: React.FC<ROIAnalysisCardProps> = ({ roiAnalysis, isLoading = false }) => {
  if (!roiAnalysis) {
    return null;
  }

  return (
    <ROIContainer>
      <ROIHeader>
        <HeaderContent>
          <ROIIcon>
            <TrendingUp size={32} />
          </ROIIcon>
          <TitleContent>
            <h2>Performance Impact Analysis</h2>
            <p>Quantified improvement metrics from AI enhancement</p>
          </TitleContent>
        </HeaderContent>
        
        <ImprovementBadge>
          <Award size={24} />
          +{roiAnalysis.percentage_improvement}% Improvement
        </ImprovementBadge>
      </ROIHeader>

      <MetricsGrid>
        <MetricCard>
          <MetricIcon>
            <Target size={20} />
          </MetricIcon>
          <MetricValue $isLoading={isLoading}>
            {isLoading ? <Loader2 size={16} className="spin" /> : roiAnalysis.total_questions_improved}
          </MetricValue>
          <MetricLabel>Questions Enhanced</MetricLabel>
        </MetricCard>

        <MetricCard>
          <MetricIcon>
            <Zap size={20} />
          </MetricIcon>
          <MetricValue $isLoading={isLoading}>
            {isLoading ? <Loader2 size={16} className="spin" /> : roiAnalysis.high_impact_improvements}
          </MetricValue>
          <MetricLabel>High Impact</MetricLabel>
        </MetricCard>

        <MetricCard>
          <MetricIcon>
            <CheckCircle size={20} />
          </MetricIcon>
          <MetricValue $isLoading={isLoading}>
            {isLoading ? <Loader2 size={16} className="spin" /> : roiAnalysis.confidence_level}
          </MetricValue>
          <MetricLabel>Confidence Level</MetricLabel>
        </MetricCard>

        <MetricCard>
          <MetricIcon>
            <BarChart3 size={20} />
          </MetricIcon>
          <MetricValue $isLoading={isLoading}>
            {isLoading ? <Loader2 size={16} className="spin" /> : 
              `${Math.round(((roiAnalysis.enhanced_score - roiAnalysis.baseline_score) / roiAnalysis.baseline_score) * 100)}%`
            }
          </MetricValue>
          <MetricLabel>Score Boost</MetricLabel>
        </MetricCard>
      </MetricsGrid>

      <ScoreComparison>
        <ScoreItem type="baseline">
          <div className="label">Baseline Score</div>
          <div className="score">{roiAnalysis.baseline_score}</div>
          <div className="description">Original submission quality</div>
        </ScoreItem>
        
        <ArrowIcon>
          <TrendingUp size={24} />
        </ArrowIcon>
        
        <ScoreItem type="enhanced">
          <div className="label">Enhanced Score</div>
          <div className="score">{roiAnalysis.enhanced_score}</div>
          <div className="description">AI-optimised quality</div>
        </ScoreItem>
      </ScoreComparison>

      <ConfidenceSection>
        <ConfidenceHeader>
          <ConfidenceIcon>
            <Award size={16} />
          </ConfidenceIcon>
          <ConfidenceTitle>Analysis Summary</ConfidenceTitle>
        </ConfidenceHeader>
        <ConfidenceContent>
          {roiAnalysis.summary}
        </ConfidenceContent>
      </ConfidenceSection>
    </ROIContainer>
  );
};

export default ROIAnalysisCard;